// components/expenses/AddExpenseDialog.jsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    CircularProgress,
    Autocomplete
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { expenseAPI } from '../../services/api';

const AddExpenseDialog = ({
    open,
    onClose,
    selectedExpense,
    onSuccess,
    categories,
    branches,
    user
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    const getUserBranch = () => {
        const userBranchId = user?.branch?._id || user?.branch;
        return branches.find(b => b._id === userBranchId) || null;
    };

    const getInitialFormState = () => {
        return {
            name: '',
            amount: '',
            date: new Date().toISOString().slice(0, 10),
            notes: '',
            category: null,
            branch: user?.isSuperAdmin ? null : getUserBranch()
        };
    };

    const [form, setForm] = useState(getInitialFormState());

    useEffect(() => {
        if (!open) return;

        if (selectedExpense) {
            setForm({
                name: selectedExpense.name || '',
                amount: selectedExpense.amount || '',
                date: selectedExpense.date ? selectedExpense.date.slice(0, 10) : '',
                notes: selectedExpense.notes || '',
                category: categories.find(c => c._id === selectedExpense.category?._id) || null,
                branch: branches.find(b => b._id === selectedExpense.branch?._id) || null
            });
        } else {
            setForm(getInitialFormState());
        }
    }, [open, selectedExpense, categories, branches, user]);

    useEffect(() => {
        if (!open || selectedExpense) return;

        if (branches.length > 0 && !user?.isSuperAdmin && !form.branch) {
            const userBranch = getUserBranch();
            if (userBranch) {
                setForm(prev => ({ ...prev, branch: userBranch }));
            }
        }
    }, [branches, open, selectedExpense, user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!form.name || !form.amount || !form.category || !form.branch || !form.date) {
            toast.error(t('common.fillRequired', 'Please fill all required fields'));
            return;
        }

        try {
            setLoading(true);
            const payload = {
                name: form.name.trim(),
                amount: Number(form.amount),
                date: form.date,
                category: form.category._id,
                branch: form.branch._id,
                notes: form.notes?.trim() || undefined
            };

            if (selectedExpense) {
                await expenseAPI.update(selectedExpense._id, payload);
                toast.success(t('expenses.updateSuccess', 'Expense updated successfully'));
            } else {
                await expenseAPI.create(payload);
                toast.success(t('expenses.createSuccess', 'Expense created successfully'));
            }
            onSuccess();
            handleClose();
        } catch (err) {
            console.error('Expense save error:', err);
            toast.error(err.response?.data?.message || t('expenses.saveError', 'Failed to save expense'));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setForm({
            name: '',
            amount: '',
            date: new Date().toISOString().slice(0, 10),
            notes: '',
            category: null,
            branch: user?.isSuperAdmin ? null : getUserBranch()
        });
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {selectedExpense
                    ? t('expenses.editTitle', 'Edit Expense')
                    : t('expenses.addTitle', 'Add Expense')
                }
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="name"
                            label={t('expenses.description', 'Description')}
                            value={form.name}
                            onChange={handleChange}
                            required
                            autoFocus
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            name="amount"
                            label={t('expenses.amount', 'Amount')}
                            type="number"
                            value={form.amount}
                            onChange={handleChange}
                            required
                            inputProps={{ min: 0, step: '0.01' }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            name="date"
                            label={t('expenses.date', 'Date')}
                            type="date"
                            value={form.date}
                            onChange={handleChange}
                            InputLabelProps={{ shrink: true }}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={categories}
                            getOptionLabel={(option) => option.name || ''}
                            value={form.category}
                            onChange={(event, newValue) => setForm(prev => ({ ...prev, category: newValue }))}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('common.category', 'Category')}
                                    required
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={branches}
                            getOptionLabel={(option) => option.name || ''}
                            value={form.branch}
                            onChange={(event, newValue) => setForm(prev => ({ ...prev, branch: newValue }))}
                            disabled={!user?.isSuperAdmin}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('common.branch', 'Branch')}
                                    required
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option._id === value?._id}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="notes"
                            label={t('expenses.notes', 'Notes')}
                            value={form.notes}
                            onChange={handleChange}
                            multiline
                            rows={3}
                            placeholder={t('expenses.notesPlaceholder', 'Optional notes...')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={20} />
                    ) : selectedExpense ? (
                        t('common.saveChanges', 'Save Changes')
                    ) : (
                        t('common.create', 'Create')
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddExpenseDialog;